// Difficulty multiplies the XP a correct answer earns. Absent difficulty is
// treated as 'medium' so legacy questions are unaffected.
export const DIFFICULTY_XP_MULTIPLIER = {
    easy: 0.75,
    medium: 1,
    hard: 1.5,
};
export function difficultyXPMultiplier(difficulty) {
    return difficulty ? DIFFICULTY_XP_MULTIPLIER[difficulty] : 1;
}
export const RANK_POINTS = [100, 75, 50, 25, 10, 5, 2, 1];
export const STREAK_BONUS_PER = 10;
export const MAX_STREAK_BONUS = 50;
export const SOLO_BASE_POINTS = 100;
export const SOLO_SPEED_BONUS_MAX = 50;
// Category XP earned per correct answer (base) — scales with question difficulty
export const CATEGORY_XP_BASE = 10;
export const CATEGORY_XP_PER_POINT = 0.2; // 20% of basePoints as bonus XP
// No rank bonus (nobody else is racing you) — just
// correctness, a speed bonus for beating the clock, and the same streak
// bonus as multiplayer so back-to-back correct answers still feel rewarded.
export function calculateSoloScore(correct, answerTimeMs, timeLimitMs, streak) {
    if (!correct)
        return 0;
    const speedFraction = Math.max(0, 1 - answerTimeMs / timeLimitMs);
    const speedBonus = Math.round(speedFraction * SOLO_SPEED_BONUS_MAX);
    const streakBonus = Math.min(streak * STREAK_BONUS_PER, MAX_STREAK_BONUS);
    return SOLO_BASE_POINTS + speedBonus + streakBonus;
}
export function calculateScore(player, round, _allPlayers, playerRank) {
    const question = round.question;
    let basePoints = 0;
    const breakdownParts = [];
    if (question.type === 'numeric') {
        const correct = Number(question.correctAnswer);
        const given = Number(round.answers[player.id]);
        if (!isNaN(given)) {
            const diff = Math.abs(given - correct);
            if (diff === 0) {
                basePoints = question.basePoints;
                breakdownParts.push(`Exact answer: +${question.basePoints}`);
            }
            else if (diff <= (question.validation?.tolerance || 0)) {
                basePoints = Math.round(question.basePoints * 0.5);
                breakdownParts.push(`Close (within tolerance): +${basePoints}`);
            }
            else {
                basePoints = 0;
                breakdownParts.push(`Wrong: +0`);
            }
        }
    }
    else if (question.type === 'order') {
        const correct = question.correctAnswer;
        const given = round.answers[player.id] ? JSON.parse(round.answers[player.id]) : [];
        let correctPositions = 0;
        given.forEach((item, idx) => {
            if (correct[idx] === item)
                correctPositions++;
        });
        basePoints = Math.round(question.basePoints * (correctPositions / correct.length));
        breakdownParts.push(`${correctPositions}/${correct.length} correct positions: +${basePoints}`);
    }
    else if (question.type === 'multiple-choice') {
        const given = round.answers[player.id];
        if (given === question.correctAnswer) {
            basePoints = question.basePoints;
            breakdownParts.push(`Correct: +${question.basePoints}`);
        }
        else {
            basePoints = 0;
            breakdownParts.push(`Wrong: +0`);
        }
    }
    else {
        const given = round.answers[player.id] || '';
        if (validateAnswer(question, given)) {
            basePoints = question.basePoints;
            breakdownParts.push(`Correct: +${question.basePoints}`);
        }
        else {
            basePoints = 0;
            breakdownParts.push(`Wrong: +0`);
        }
    }
    // Rank bonus (speed) only rewards players who actually scored base points —
    // being fastest with a wrong answer shouldn't earn anything.
    const rankBonus = basePoints > 0 && playerRank <= RANK_POINTS.length ? RANK_POINTS[playerRank - 1] : 0;
    if (rankBonus > 0)
        breakdownParts.push(`Rank #${playerRank}: +${rankBonus}`);
    // Like the rank bonus, a streak only pays out on a correct answer.
    let streakBonus = 0;
    if (basePoints > 0 && player.streak > 0) {
        streakBonus = Math.min(player.streak * STREAK_BONUS_PER, MAX_STREAK_BONUS);
        if (streakBonus > 0)
            breakdownParts.push(`${player.streak}🔥 streak: +${streakBonus}`);
    }
    const subtotal = basePoints + rankBonus + streakBonus;
    const total = subtotal;
    // Category XP — only for correct multiple-choice answers in multiplayer
    let categoryXP = 0;
    if (basePoints > 0 && question.type === 'multiple-choice') {
        const raw = CATEGORY_XP_BASE + question.basePoints * CATEGORY_XP_PER_POINT;
        categoryXP = Math.round(raw * difficultyXPMultiplier(question.difficulty));
    }
    return {
        playerId: player.id,
        basePoints,
        rankBonus,
        streakBonus,
        total,
        breakdown: breakdownParts.join(' | ') || 'No points',
        categoryXP,
    };
}
export function validateAnswer(question, answer) {
    if (!question.correctAnswer)
        return false;
    const correct = Array.isArray(question.correctAnswer)
        ? question.correctAnswer.map(c => c.toString().trim().toLowerCase())
        : [question.correctAnswer.toString().trim().toLowerCase()];
    const given = answer.trim().toLowerCase();
    if (question.validation?.caseSensitive) {
        return Array.isArray(question.correctAnswer)
            ? question.correctAnswer.some(c => answer.trim() === c.toString().trim())
            : answer.trim() === question.correctAnswer.toString().trim();
    }
    if (question.validation?.fuzzyMatch) {
        return correct.some(c => given.includes(c) || c.includes(given) || levenshtein(given, c) <= 2);
    }
    return correct.some(c => given === c);
}
export function levenshtein(a, b) {
    const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));
    for (let i = 0; i <= a.length; i++)
        matrix[0][i] = i;
    for (let j = 0; j <= b.length; j++)
        matrix[j][0] = j;
    for (let j = 1; j <= b.length; j++) {
        for (let i = 1; i <= a.length; i++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            matrix[j][i] = Math.min(matrix[j][i - 1] + 1, matrix[j - 1][i] + 1, matrix[j - 1][i - 1] + cost);
        }
    }
    return matrix[b.length][a.length];
}
